package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.AutomationRule
import com.dotdrives.model.NewAutomationRule
import com.dotdrives.model.containsMoneyToken
import com.dotdrives.repository.AutomationRuleRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/** No trigger token here is a money value -- see [containsMoneyToken] and docs/schema.md. */
data class AutomationRuleDraft(
    val name: String = "",
    val triggerVerb: String? = DEFAULT_TRIGGER_VERB,
    val minAmountCents: Long? = null,
    val assignee: String = "steward",
    val descriptionTemplate: String = DEFAULT_DESCRIPTION_TEMPLATE,
    val dueInDays: Int = 2,
)

data class AutomationRulesUiState(
    val loading: Boolean = false,
    val rules: List<AutomationRule> = emptyList(),
    val error: String? = null,
    val builderOpen: Boolean = false,
    val draft: AutomationRuleDraft = AutomationRuleDraft(),
    val previewCount: Int? = null,
    /** False when the last preview could not be fully verified (RLS hid matching donation
     * rows from this viewer) -- the count above is a lower bound, not a confident answer. */
    val previewComplete: Boolean = true,
    val previewLoading: Boolean = false,
    val saving: Boolean = false,
    val builderError: String? = null,
)

class AutomationRulesController(
    private val repo: AutomationRuleRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<AutomationRulesUiState>(AutomationRulesUiState(), scope) {
    private var generation = 0L
    private var previewGeneration = 0L

    fun load() {
        val current = nextGeneration()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                repo.list(orgId)
                    .onSuccess { rules ->
                        if (!isCurrent(current)) return@onSuccess
                        // trigger_verb == null (the two condition rules) is a valid row shape,
                        // not an error -- it must reach the UI untouched (docs/schema.md).
                        update { it.copy(loading = false, rules = rules, error = null) }
                    }
                    .onFailure { publishError(it, current) }
            } catch (error: Exception) {
                publishError(error, current)
            }
        }
    }

    fun retry() = load()

    fun setEnabled(ruleId: String, enabled: Boolean) {
        val previous = state.value.rules
        val existing = previous.firstOrNull { it.id == ruleId } ?: return
        update {
            it.copy(
                rules = it.rules.map { r -> if (r.id == ruleId) r.copy(isEnabled = enabled) else r },
                error = null,
            )
        }
        scope.launch {
            repo.setEnabled(orgId, ruleId, enabled).onFailure { error ->
                if (error is CancellationException) throw error
                update {
                    it.copy(
                        rules = it.rules.map { r -> if (r.id == ruleId) existing else r },
                        error = error.message ?: "Something went wrong",
                    )
                }
            }
        }
    }

    fun openBuilder() {
        update {
            it.copy(
                builderOpen = true,
                draft = AutomationRuleDraft(),
                previewCount = null,
                previewComplete = true,
                previewLoading = false,
                builderError = null,
            )
        }
        // The brief names this preview explicitly -- it must be visible the moment the
        // builder opens (for the default trigger), not only after the user types an amount.
        refreshPreview()
    }

    fun closeBuilder() {
        update { it.copy(builderOpen = false, builderError = null) }
    }

    fun setDraftName(name: String) {
        update { it.copy(draft = it.draft.copy(name = name), builderError = null) }
    }

    fun setDraftTriggerVerb(verb: String?) {
        update { it.copy(draft = it.draft.copy(triggerVerb = verb), builderError = null) }
        refreshPreview()
    }

    fun setDraftMinAmountCents(cents: Long?) {
        update { it.copy(draft = it.draft.copy(minAmountCents = cents), builderError = null) }
        refreshPreview()
    }

    fun setDraftAssignee(assignee: String) {
        update { it.copy(draft = it.draft.copy(assignee = assignee), builderError = null) }
    }

    fun setDraftDueInDays(days: Int) {
        update { it.copy(draft = it.draft.copy(dueInDays = days.coerceAtLeast(0)), builderError = null) }
    }

    /**
     * Templates are member-readable and copied verbatim into `reminder.created`'s payload --
     * a money token here would leak donation amounts to a `field` rep. Reject at the source:
     * a template containing one never reaches the draft, so the "+ insert" affordance the view
     * offers can never have put it there either (docs/schema.md).
     */
    fun setDraftDescriptionTemplate(text: String) {
        if (containsMoneyToken(text)) {
            update { it.copy(builderError = MONEY_TOKEN_MESSAGE) }
            return
        }
        update { it.copy(draft = it.draft.copy(descriptionTemplate = text), builderError = null) }
    }

    fun save() {
        val current = state.value
        if (current.saving) return
        val draft = current.draft
        if (draft.name.isBlank()) {
            update { it.copy(builderError = "Give this rule a name") }
            return
        }
        if (draft.descriptionTemplate.isBlank()) {
            update { it.copy(builderError = "Add what the step should say") }
            return
        }
        if (containsMoneyToken(draft.descriptionTemplate)) {
            update { it.copy(builderError = MONEY_TOKEN_MESSAGE) }
            return
        }
        update { it.copy(saving = true, builderError = null) }
        scope.launch {
            repo.create(
                orgId,
                NewAutomationRule(
                    name = draft.name.trim(),
                    triggerVerb = draft.triggerVerb,
                    minAmountCents = draft.minAmountCents,
                    assignee = draft.assignee,
                    descriptionTemplate = draft.descriptionTemplate,
                    dueInDays = draft.dueInDays,
                ),
            )
                .onSuccess { saved ->
                    update {
                        it.copy(
                            saving = false,
                            rules = it.rules + saved,
                            builderOpen = false,
                            draft = AutomationRuleDraft(),
                            previewCount = null,
                            previewComplete = true,
                        )
                    }
                }
                .onFailure { error ->
                    update { it.copy(saving = false, builderError = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun refreshPreview() {
        val current = nextPreviewGeneration()
        val verb = state.value.draft.triggerVerb
        if (verb == null) {
            update { it.copy(previewCount = null, previewComplete = true, previewLoading = false) }
            return
        }
        val minAmountCents = state.value.draft.minAmountCents
        update { it.copy(previewLoading = true) }
        scope.launch {
            repo.previewFireCount(orgId, verb, minAmountCents)
                .onSuccess { result ->
                    if (isCurrentPreview(current)) {
                        update {
                            it.copy(
                                previewCount = result.count,
                                previewComplete = result.complete,
                                previewLoading = false,
                            )
                        }
                    }
                }
                .onFailure {
                    if (it is CancellationException) throw it
                    if (isCurrentPreview(current)) update { s -> s.copy(previewLoading = false) }
                }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { it.copy(loading = false, error = error.message ?: "Something went wrong") }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(candidate: Long): Boolean = candidate == generation

    private fun nextPreviewGeneration(): Long {
        previewGeneration += 1
        return previewGeneration
    }

    private fun isCurrentPreview(candidate: Long): Boolean = candidate == previewGeneration
}

private const val DEFAULT_TRIGGER_VERB = "gift.recorded"
private const val DEFAULT_DESCRIPTION_TEMPLATE = "Call to say thank you — {giver} sent a gift 💛"
private const val MONEY_TOKEN_MESSAGE =
    "Templates can't include a dollar-amount token like {amount} — open the giver record for the number."
